import { gql } from "lib/apolloClient";
import {
  staffFragment,
  studentFragment,
  attachmentFragment,
  resolvedFieldValueFragment,
  taggedPracticesFragment,
  academicYearFragment,
  curriculumProgramFragment,
  expertProfileFragment,
  userNameFragment,
} from "modules/CommonFragments";
import { organizationResourceFragment } from "OrganizationResources/modules/OrganizationResourcesFragments";

// Because of circular dependency defined here
export const projectAttachmentFragment = {
  attachment: gql`
    fragment attachmentItemV2 on Attachment {
      ...attachmentItem
      isAnnotationRead
    }
    ${attachmentFragment.attachment}
  `,
};

export const pypElementNodeFragment = {
  focusArea: gql`
    fragment focusAreaItem on FocusArea {
      id
      label
    }
  `,
  subjectGroupBasicDetails: gql`
    fragment subjectGroupBasicDetailsItem on SubjectGroup {
      id
      name
      constants
    }
  `,
  genericTag: gql`
    fragment basicGenericTagItem on GenericTag {
      id
      type
      label
    }
  `,
  standardSet: gql`
    fragment standardSetItem on BenchmarkStandardSet {
      id
      title
      description
      code
    }
  `,
};
export const pypElementFragment = {
  theme: gql`
    fragment themeItem on PlannerTheme {
      id
      label
      descriptor
      metadata
      associatedChildren {
        id
        label
      }
      focusAreas {
        ...focusAreaItem
      }
      iconUrl: icon
    }
    ${pypElementNodeFragment.focusArea}
  `,
  lp: gql`
    fragment lpItem on PlannerLearnerProfile {
      id
      label
      subText
      levelId
      parent
      children
    }
  `,
  concept: gql`
    fragment conceptItem on PlannerConcept {
      id
      label
      subText
      levelId
      parent
      children
    }
  `,
  atl: gql`
    fragment atlItem on PlannerATL {
      id
      label
      children
      parent
      levelId
      levels {
        id
        value
      }
      depth
      isLeaf
      displaySequence
      atlCategory {
        id
      }
      subText
      dataSetNodeWrapper {
        properties {
          isArchived
          grades {
            id
            name
          }
        }
      }
    }
  `,
  benchmark: gql`
    fragment benchmarkItem on PlannerBenchmark {
      id
      label
      subText
      children
      parent
      subject
      displaySequence
      depth
      code
      dataSetNodeWrapper {
        standardSet {
          id
          title
        }
        properties {
          grades {
            id
            name
          }
          subject {
            id
            name
            isArchived
          }
        }
      }
    }
  `,
  relatedConcept: gql`
    fragment relatedConceptItem on PlannerRelatedConcept {
      id
      label
      elementId
      elementType
      parent
      children
    }
  `,
  action: gql`
    fragment actionItem on Action {
      id
      label
      subText
      children
      parent
      levels {
        id
        value
      }
      levelId
    }
  `,
  plannerElementNode: gql`
    fragment plannerElementNodeItem on PlannerElementNode {
      id
      displaySequence
      label
      subText
      code
      type
      depth
      children
      parent
      subType
      updatedAt
      dataSetNodeWrapper {
        properties {
          subjects {
            id
            name
            isArchived
          }
          grades {
            id
            name
          }
        }
      }
    }
  `,
  attachmentGroup: gql`
    fragment attachmentGroupItem on AttachmentGroup {
      id
      metadata
      attachments {
        ... on Attachment {
          id
          name
          type
          mimeType
          url
          signedUrl
          thumbUrl
          title
        }
      }
      studentTemplate {
        id
      }
    }
  `,
  attachment: gql`
    fragment attachmentItem on Attachment {
      id
      name
      type
      mimeType
      url
      signedUrl
      thumbUrl
      title
      metadata
      streamUrl
      parentType
      isRead
      similarityReport {
        status
        submissionId
        errorCode
        overallMatchPercentage
      }
      createdBy {
        id
        type
      }
      lockingState {
        state
        scheduledAt
      }
      questionAttachment {
        ... on QuestionSubmission {
          id
          questionScore
          score {
            id
            value
          }
        }
        ... on Question {
          id
          questionScore
        }
      }
    }
  `,
  fieldScore: gql`
    fragment fieldScoreItem on ResolvedFieldScore {
      id
      type
      isUsingScore
      maxScore
      category {
        id
        label
        colorCode
        set {
          id
          associatedEntities {
            id
            ... on Course {
              id
              title
            }
            ... on Subject {
              id
              name
            }
          }
        }
      }
    }
  `,
  subjectGroupWithGlobalSubjectGroup: gql`
    fragment subjectGroupWithGlobalSubjectGroupItem on SubjectGroup {
      id
      name
      constants
      globalSubjectGroup {
        id
        name
        constants
      }
    }
  `,
};

export const plannerElementNodeFragment = {
  nonPypPlannerElementNode: gql`
    fragment nonPypPlannerElementNodeItem on PlannerElementNode {
      id
      displaySequence
      label
      subText
      code
      type
      depth
      children
      parent
      updatedAt
      # icon used in customDatasets
      icon {
        id
        title
        url
        metadata
      }
      dataSetNodeWrapper {
        plannerElementNodeSet {
          id
          title
        }
        properties {
          isArchived
          grades {
            id
            name
          }
          subjects {
            id
            name
            isArchived
          }
          tags {
            id
            label
          }
          subjectGroups {
            ...subjectGroupBasicDetailsItem
          }
          associatedPlannerElementNodes {
            id
            label
            type
            metadata
          }
        }
      }
    }
    ${pypElementNodeFragment.subjectGroupBasicDetails}
  `,

  nonPypPlannerElementNodeResolvedValue: gql`
    fragment nonPypPlannerElementNodeResolvedValueItem on PlannerElementNode {
      id
      displaySequence
      label
      subText
      code
      type
      depth
      children
      parent
      updatedAt
      dataSetNodeWrapper {
        plannerElementNodeSet {
          id
          title
        }
        properties {
          isArchived
          grades {
            id
            name
          }
          subjects {
            id
            name
            isArchived
          }
          tags {
            id
            label
          }
          subjectGroups {
            ...subjectGroupBasicDetailsItem
          }
          associatedPlannerElementNodes {
            id
            label
            type
            metadata
          }
        }
      }
    }
    ${pypElementNodeFragment.subjectGroupBasicDetails}
  `,
};

export const plannerElementNodeSetWrapperFragment = {
  plannerElementNodeSetWrapperV2: gql`
    fragment plannerElementNodeSetWrapperV2Item on PlannerElementNodeSet {
      plannerElementNodeSetWrapperV2 @skip(if: $shouldSkipNodes) {
        ... on PlannerElementNodeSetWrapper {
          publishedData(filters: $plannerElementNodeFilters) {
            edges {
              node {
                ...nonPypPlannerElementNodeItem
              }
            }
          }
        }
        ... on PlannerActionSetWrapper {
          publishedData {
            edges {
              node {
                ...actionItem
              }
            }
          }
        }
        ... on PlannerLearnerProfileSetWrapper {
          publishedData {
            edges {
              node {
                ...lpItem
              }
            }
          }
        }
        ... on PlannerConceptSetWrapper {
          publishedData {
            edges {
              node {
                ...conceptItem
              }
            }
          }
        }
        ... on PlannerRelatedConceptSetWrapper {
          publishedData(filters: $plannerElementNodeFilters) {
            edges {
              node {
                ...relatedConceptItem
              }
            }
          }
        }
        ... on PlannerThemeSetWrapper {
          publishedData(filters: { academicYear: $academicYearId }) {
            edges {
              node {
                ...themeItem
              }
            }
          }
        }
      }
    }
    ${plannerElementNodeFragment.nonPypPlannerElementNode}
    ${pypElementFragment.action}
    ${pypElementFragment.lp}
    ${pypElementFragment.concept}
    ${pypElementFragment.relatedConcept}
    ${pypElementFragment.theme}
  `,
};

export const plannerElementNodeSetFragment = {
  plannerElementNodeSet: gql`
    fragment plannerElementNodeSetItem on PlannerElementNodeSetConnection {
      edges {
        node {
          id
          type
          plannerElementSet {
            id
            type
            config {
              id
              config
              valueType
              value
            }
          }
        }
      }
    }
  `,
  plannerElementNodeSetWithOptionalNodes: gql`
    fragment plannerElementNodeSetWithOptionalNodesItem on PlannerElementNodeSetConnection {
      edges {
        node {
          id
          type
          plannerElementSet {
            id
            type
          }
          ...plannerElementNodeSetWrapperV2Item
        }
      }
    }
    ${plannerElementNodeSetWrapperFragment.plannerElementNodeSetWrapperV2}
  `,
  plannerElementNodeSetWithNodes: gql`
    fragment plannerElementNodeSetWithNodesItem on PlannerElementNodeSet {
      id
      type
      plannerElementSet {
        id
        type
        config {
          id
          config
          valueType
          value
        }
      }
      ...plannerElementNodeSetWrapperV2Item
    }
    ${plannerElementNodeSetWrapperFragment.plannerElementNodeSetWrapperV2}
  `,
};

export const subjectGroupFragment = {
  subjectBenchmarkDetails: gql`
    fragment subjectItem on Subject {
      id
      name
      icon
      grades {
        id
        name
      }
      isArchived
      benchmarkRootNode {
        ...benchmarkItem
      }
      globalSubject
      framework
      snsChildren
      standardSet {
        ...standardSetItem
      }
      subjectLevelLabels {
        cuLevelName
        strandLevelName
        loLevelName
      }
      type
      tags {
        id
        key
        value
      }
      associatedLevels {
        id
        label
        isArchived
      }
      variants {
        level {
          ...basicGenericTagItem
        }
        responseLanguages {
          ...basicGenericTagItem
        }
      }
      flatVariants(filters: { archivalState: ALL }) {
        isArchived
        level {
          ...basicGenericTagItem
        }
        responseLanguage {
          ...basicGenericTagItem
        }
      }
    }
    ${pypElementFragment.benchmark}
    ${pypElementNodeFragment.genericTag}
    ${pypElementNodeFragment.standardSet}
  `,
};
export const resolvedFieldValueItemFragment = gql`
  fragment resolvedFieldValueItem on ResolvedFieldValue {
    id
    type
    ... on ResolvedFieldBenchmarkSet {
      id
      benchmarks {
        ...benchmarkItem
      }
    }
    ... on ResolvedFieldConceptSet {
      id
      concepts {
        ...conceptItem
      }
    }
    ... on ResolvedFieldActionSet {
      id
      actions {
        ...actionItem
      }
    }
    ... on ResolvedFieldLPSet {
      id
      learnerProfiles {
        ...lpItem
      }
    }
    ... on ResolvedFieldThemeSet {
      id
      themes {
        ...themeItem
      }
    }
    ... on ResolvedFieldSubjectSet {
      id
      subjects {
        ...subjectItem
        subjectGroup {
          id
          name
          constants
          globalSubjectGroup {
            id
            name
            constants
          }
        }
      }
    }
    ... on ResolvedFieldATLSet {
      id
      atls {
        ...atlItem
      }
    }
    ... on ResolvedFieldRelatedConceptSet {
      id
      relatedConcepts {
        ...relatedConceptItem
      }
    }
    ... on ResolvedFieldVideoAttachment {
      id
      attachment {
        ...attachmentItem
      }
    }
    ... on ResolvedFieldStudentTemplate {
      id
      attachmentGroups {
        ...attachmentGroupItem
      }
    }
    ... on ResolvedFieldTags {
      id
      tags {
        id
        label
      }
    }
    ... on ResolvedFieldGenericTags {
      id
      genericTags {
        id
        label
        abbreviation
        __typename
      }
      __typename
    }
    ... on ResolvedFieldPlannerElementSet {
      id
      type
      nodes {
        ...nonPypPlannerElementNodeResolvedValueItem
      }
    }
    ... on ResolvedFieldSubjectGroup {
      id
      type
      subjectGroups {
        ...subjectGroupWithGlobalSubjectGroupItem
      }
    }
    ... on ResolvedFieldScore {
      ...fieldScoreItem
    }
    ... on ResolvedFieldObjectiveLevel {
      id
      nodes {
        id
        label
        dataSetNodeWrapper {
          properties {
            subjects {
              id
            }
          }
        }
      }
    }
    ... on ResolvedFieldAttachments {
      id
      type
      attachments {
        ...attachmentItem
      }
    }
  }
  ${plannerElementNodeFragment.nonPypPlannerElementNodeResolvedValue}
  ${pypElementFragment.benchmark}
  ${pypElementFragment.concept}
  ${pypElementFragment.action}
  ${pypElementFragment.lp}
  ${pypElementFragment.theme}
  ${pypElementFragment.atl}
  ${pypElementFragment.relatedConcept}
  ${pypElementFragment.fieldScore}
  ${pypElementFragment.attachment}
  ${pypElementFragment.attachmentGroup}
  ${pypElementFragment.subjectGroupWithGlobalSubjectGroup}
  ${subjectGroupFragment.subjectBenchmarkDetails}
`;
export const resolvedFieldValueFragmentV2 = {
  resolvedMinimalTree: gql`
    fragment unitPlanResolvedMinimalTreeItem on ResolvedFieldValue {
      ...resolvedFieldValueItem
    }
    ${resolvedFieldValueItemFragment}
  `,
};
export const resourceLibraryFragment = {
  resourceFeed: gql`
    fragment resourceLibraryItem on FlowSectionItem {
      id
      title {
        id
        value
      }
      image {
        id
        value
      }
      duration {
        id
        value
      }
      createdBy {
        ...staffItem
      }
      templateId
      ... on Assessment {
        assessmentType {
          id
          value
        }
        taskType {
          id
          type
          label
          color
          displaySequence
        }
        academicTerm {
          id
          lockingStatus
        }
        assessmentTools {
          edges {
            node {
              id
              assessmentToolType
            }
          }
        }
        unitFlowResources {
          id
        }
        category {
          id
          label
        }
        grades {
          id
          name
        }
        fields(
          uids: ["duration", "size", "measureAssessing", "assessmentType"]
        ) {
          id
          uid
          value
        }
        isImported
        taggedPractices {
          ...taggedPracticesOnEvidence
        }
      }
      ... on LearningEngagement {
        fields(uids: ["duration", "size", "documentation"]) {
          id
          uid
          value
        }
      }
    }
    ${taggedPracticesFragment.taggedPractices}
    ${staffFragment.basicStaffDetails}
  `,
};

const unitPlanFeed = gql`
  fragment unitPlanItem on UnitPlan {
    id
    state
    startDate
    endDate
    isPublishedInSampleUnits
    isImported
    isSharedWithStudents
    isSharedWithFamilies
    createdBy {
      ...userNameFields
      profileImage
    }
    curriculumProgram {
      ...curriculumProgramBasicDetailsItem
    }
    owner {
      ...userNameFields

      profileImage
    }
    title {
      id
      value
    }
    unitCode {
      id
      value
    }
    courses {
      id
      title
    }
    grades
    resolvedGrades {
      id
      name
    }
    theme {
      id
      value
      resolvedMinimalTree {
        ... on ResolvedFieldThemeSet {
          id
          themes {
            id
            label
            __typename
          }
          __typename
        }
        __typename
      }
    }
    duration {
      id
      value
    }
    unitType {
      id
      value
    }
    image {
      id
      value
      resolvedMinimalTree {
        ...resolvedFieldVideoAttachmentItem
      }
    }
    templateId
    subjects {
      id
      value
      resolvedMinimalTree {
        ... on ResolvedFieldSubjectSet {
          id
          subjects {
            id
            name
            type
            isArchived
            __typename
          }
          __typename
        }
        __typename
      }
    }
    expertProfile {
      ...expertItem
    }
    resourceLibrary {
      leCount
      assessmentCount
    }
    assessmentTemplate
    leTemplate
    collaborators: allCollaborators(filters: $collaboratorsFilter) {
      totalCount
      isEditPermission
      edges @include(if: $showCollaborators) {
        permission
        node {
          ...userNameFields
          profileImage
        }
      }
    }
    taggedPractices {
      ...taggedPracticesOnEvidence
    }
    academicYears {
      ...academicYearItem
    }
    submittedToCommunity
    fields(uids: ["hours", "subjectLevel", "objectiveLevel"]) {
      id
      uid
      value
      resolvedMinimalTree {
        ...unitPlanResolvedMinimalTreeItem
      }
    }
    createdAt
    updatedAt
    centralRepoConfig(input: $centralRepoInput) {
      newVersionId
      isSeen
      derivedStatus
      publishedDate
    }
    cascadeUpdatedBy {
      id
      firstName
      lastName
      profileImage
    }
    cascadeUpdateAt
    learningCourse {
      id
      type
      title
    }
    lockingInfo {
      isLocked
    }
  }

  ${userNameFragment}
  ${academicYearFragment.academicYear}
  ${curriculumProgramFragment.curriculumProgramBasicDetails}
  ${expertProfileFragment.expert}
  ${taggedPracticesFragment.taggedPractices}
  ${resolvedFieldValueFragmentV2.resolvedMinimalTree}
  ${resolvedFieldValueFragment.resolvedFieldVideoAttachment}
`;

export const unitPlanFragmentV2 = {
  curriculumProgramDetails: gql`
    fragment curriculumProgramBasicDetailsItem on CurriculumProgram {
      id
      type
      nodeInterfaceType
      label
      acronym
    }
  `,
  academicYearItem: gql`
    fragment academicYearItem on AcademicYear {
      id
      startDate
      endDate
      isCurrentAcademicYear
      isUpcomingAcademicYear
      transitionReviewStatus
    }
  `,
  taggedPractices: gql`
    fragment taggedPracticesOnEvidence on SnPTaggedPractice {
      evaluationCycle {
        id
      }
      taggedPractice {
        taggedBy {
          id
        }
        practice {
          id
        }
        evidenceStatus
      }
    }
  `,
  fieldSubject: gql`
    fragment resolvedFieldSubject on ResolvedFieldSubjectSet {
      id
      subjects {
        id
        name
        type
        isArchived
        associatedLevels {
          id
          label
          isArchived
        }
        __typename
      }
      __typename
    }
  `,
  fieldTheme: gql`
    fragment resolvedFieldTheme on ResolvedFieldThemeSet {
      id
      themes {
        id
        label
        __typename
      }
      __typename
    }
  `,
  fieldVideoAttachment: gql`
    fragment resolvedFieldVideoAttachmentItem on ResolvedFieldVideoAttachment {
      id
      type
      attachment {
        id
        name
        type
        url
        thumbUrl
      }
    }
  `,
};

export const unitPlanItemFragment = {
  unitPlanItem: gql`
    fragment unitPlanItem on UnitPlan {
      id
      state
      startDate
      endDate
      isPublishedInSampleUnits
      isImported
      isSharedWithStudents @include(if: $showSharedWithStudentsFamily)
      isSharedWithFamilies @include(if: $showSharedWithStudentsFamily)
      curriculumProgram {
        ...curriculumProgramBasicDetailsItem
      }
      owner {
        ...userNameFields
        profileImage
      }
      createdBy {
        ...userNameFields
        profileImage
      }
      title {
        id
        value
      }
      unitCode {
        id
        value
      }
      courses {
        id
        title
      }
      grades
      unitType {
        id
        value
      }
      image {
        id
        value
        resolvedMinimalTree {
          ...resolvedFieldVideoAttachmentItem
        }
      }
      templateId
      exceptionalCollaborators(filters: $exceptionalCollaboratorsFilter) {
        totalCount
        edges {
          node {
            id
          }
          permission
        }
      }
      taggedPractices {
        ...taggedPracticesOnEvidence
      }
      academicYears {
        ...academicYearItem
      }
      submittedToCommunity
      fields(uids: $uids) {
        id
        uid
        value
        resolvedMinimalTree {
          ...unitPlanResolvedMinimalTreeItem
        }
      }
      createdAt
      updatedAt
      centralRepoConfig(input: $centralRepoInput) {
        newVersionId
        isSeen
        derivedStatus
        publishedDate
      }
      cascadeUpdatedBy {
        id
        firstName
        lastName
        profileImage
      }
      cascadeUpdateAt
      classDates(filters: $classFilter) {
        classId
        startDate
        endDate
      }
    }
    ${userNameFragment}
    ${unitPlanFragmentV2.curriculumProgramDetails}
    ${unitPlanFragmentV2.academicYearItem}
    ${unitPlanFragmentV2.taggedPractices}
    ${unitPlanFragmentV2.fieldVideoAttachment}
    ${resolvedFieldValueFragmentV2.resolvedMinimalTree}
  `,
};

export const unitPlanFeedFragment = {
  unitPlanFeed,
  unitPlanFeedWithExceptionalCollaborators: gql`
    fragment unitPlanFeedWithExceptionalCollaboratorsItem on UnitPlan {
      ...unitPlanItem
      defaultStaffAccess
      enabledSections {
        userType
        sections
      }
      exceptionalCollaborators(filters: $exceptionalCollaboratorsFilter) {
        totalCount
        edges {
          node {
            id
          }
          permission
        }
      }
    }
    ${unitPlanFeed}
  `,
  unitPlanFeedV2: gql`
    fragment unitPlanFeedItem on UnitPlan {
      ...unitPlanItem
      defaultStaffAccess
      enabledSections {
        userType
        sections
      }
      duration @include(if: $showWeeksDuration) {
        id
        value
      }
      resolvedGrades {
        id
        name
      }
      resourceLibrary {
        leCount @include(if: $showLE)
        assessmentCount @include(if: $showAssessments)
      }
      collaborators: allCollaborators(filters: $collaboratorsFilter) {
        totalCount
        isEditPermission
        edges @include(if: $showCollaborators) {
          permission
          node {
            ...userNameFields
            profileImage
          }
        }
      }
      subjects @include(if: $showSubjects) {
        id
        value
        resolvedMinimalTree {
          ...resolvedFieldSubject
        }
      }
      theme @include(if: $showSubjects) {
        id
        value
        resolvedMinimalTree {
          ...resolvedFieldTheme
        }
      }
    }
    ${userNameFragment}
    ${unitPlanItemFragment.unitPlanItem}
    ${unitPlanFragmentV2.fieldTheme}
    ${unitPlanFragmentV2.fieldSubject}
  `,
  unitPlanCustomFeed: gql`
    fragment unitPlanFeedItem on UnitPlan {
      id
      title {
        id
        value
      }
      image {
        id
        value
        resolvedMinimalTree {
          ...resolvedFieldVideoAttachmentItem
        }
      }
      subjects {
        id
        value
        resolvedMinimalTree {
          ...resolvedFieldSubject
        }
      }
      theme {
        id
        value
        resolvedMinimalTree {
          ...resolvedFieldTheme
        }
      }
    }
    ${unitPlanFragmentV2.fieldTheme}
    ${unitPlanFragmentV2.fieldVideoAttachment}
    ${unitPlanFragmentV2.fieldSubject}
  `,
  unitPlanEvidenceFeed: gql`
    fragment unitPlanItem on UnitPlan {
      id
      state
      curriculumProgram {
        ...curriculumProgramBasicDetailsItem
      }
      title {
        id
        value
      }
      resolvedGrades {
        id
        name
      }
      theme {
        id
        value
        resolvedMinimalTree {
          ...unitPlanResolvedMinimalTreeItem
        }
      }
      unitType {
        id
        value
      }
      image {
        id
        value
        resolvedMinimalTree {
          ...unitPlanResolvedMinimalTreeItem
        }
      }
      subjects {
        id
        value
        resolvedMinimalTree {
          ...unitPlanResolvedMinimalTreeItem
        }
      }
      templateId
    }
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
    ${resolvedFieldValueFragmentV2.resolvedMinimalTree}
  `,
};

export const organizationUnitPlanFeedFragment = {
  organizationUnitPlanFeed: gql`
    fragment organizationUnitPlanFeedItem on Organization {
      id
      unitPlans(
        type: $type
        scheduledStatus: $scheduledStatus
        grades: $grades
        searchText: $searchText
        academicYears: $academicYears
        users: $users
        createdBy: $createdBy
        curriculumProgramIds: $curriculumProgramIds
        subjects: $subjects
      ) {
        totalCount
        edges {
          node {
            ...unitPlanItem
          }
        }
      }
    }
    ${unitPlanFeedFragment.unitPlanFeed}
  `,
  organizationUnitPlanFeedV2: gql`
    fragment organizationUnitPlanFeedItem on Organization {
      id
      unitPlans: unitPlansV2(first: 24, after: $after, filters: $filters) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...unitPlanFeedWithExceptionalCollaboratorsItem
          }
        }
      }
    }
    ${unitPlanFeedFragment.unitPlanFeedWithExceptionalCollaborators}
  `,
  organizationUnitPlanFeedCount: gql`
    fragment organizationUnitPlanFeedCountItem on Organization {
      id
      unitPlans(
        type: $type
        scheduledStatus: $scheduledStatus
        grades: $grades
        searchText: $searchText
        academicYears: $academicYears
        users: $users
      ) {
        totalCount
      }
    }
  `,
};

export const evidenceResponseFeedPostFragment = {
  postEvidence: gql`
    fragment postItem on Post {
      id
      title
      attachments {
        ...attachmentItem
      }
      items {
        item {
          id
          ... on StudentAssignment {
            id
            assignment {
              id
              content {
                id
                ... on Assessment {
                  id
                  assessmentType {
                    id
                    value
                  }
                }
              }
            }
          }
        }
        itemType
      }
    }
    ${attachmentFragment.attachment}
  `,
};

export const responseFragment = {
  response: gql`
    fragment responseItem on Response {
      id
      message
      createdBy {
        id
        ... on Staff {
          ...staffItem
        }
        ... on Student {
          ...studentItem
        }
      }
      evidence {
        id
        ... on Attachment {
          ...attachmentItem
        }
        ... on Post {
          id
          title
          attachments {
            id
            name
            type
            signedUrl
          }
          items {
            item {
              id
              ... on StudentAssignment {
                id
                assignment {
                  id
                  content {
                    id
                    ... on Assessment {
                      id
                      assessmentType {
                        id
                        value
                      }
                    }
                  }
                }
              }
            }
            itemType
          }
        }
      }
      createdAt
      updatedAt
    }
    ${staffFragment.basicStaffDetails}
    ${studentFragment.basicStudentDetails}
    ${attachmentFragment.attachment}
  `,
};

export const plannerFieldFragment = {
  unitplanField: gql`
    fragment unitPlanFieldItem on PlannerField {
      id
      uid
      value
      comments {
        totalCount
        unreadCount
      }
      resolvedMinimalTree {
        ...unitPlanResolvedMinimalTreeItem
      }
      responses: allResponses {
        totalCount
        edges {
          node {
            ...responseItem
          }
        }
      }
    }
    ${responseFragment.response}
    ${resolvedFieldValueFragmentV2.resolvedMinimalTree}
  `,
  unitPlanFieldWithResponseFilter: gql`
    fragment unitPlanFieldItemWithResponseFilter on PlannerField {
      id
      uid
      value
      comments {
        totalCount
        unreadCount
      }
      resolvedMinimalTree {
        ...unitPlanResolvedMinimalTreeItem
      }
      responses: allResponses(filters: $responseFilter) {
        totalCount
        edges {
          node {
            ...responseItem
          }
        }
      }
    }
    ${responseFragment.response}
    ${resolvedFieldValueFragmentV2.resolvedMinimalTree}
  `,
  unitPlanFieldWithResponseFilterV2: gql`
    fragment unitPlanFieldItemWithResponseFilter on PlannerField {
      id
      uid
      value
      resolvedMinimalTree {
        ...unitPlanResolvedMinimalTreeItem
      }
    }
    ${resolvedFieldValueFragmentV2.resolvedMinimalTree}
  `,
  unitPlanFieldResponses: gql`
    fragment unitPlanFieldItem on PlannerField {
      id
      uid
      responses: allResponses(filters: $responseFilter) {
        totalCount
        edges {
          node {
            ...responseItem
          }
        }
      }
    }
    ${responseFragment.response}
  `,
  resourceLibraryField: gql`
    fragment resourceLibraryFieldItem on PlannerField {
      id
      uid
      value
      resolvedMinimalTree {
        ...unitPlanResolvedMinimalTreeItem
      }
    }
    ${resolvedFieldValueFragmentV2.resolvedMinimalTree}
  `,
  unitplanFieldBasicDetails: gql`
    fragment unitplanFieldBasicDetailsItem on PlannerField {
      id
      uid
      value

      resolvedMinimalTree {
        ...unitPlanResolvedMinimalTreeItem
      }
    }

    ${resolvedFieldValueFragmentV2.resolvedMinimalTree}
  `,
  assessmentFieldItem: gql`
    fragment assessmentFieldItem on PlannerField {
      id
      value
    }
  `,

  projectFieldItem: gql`
    fragment projectFieldItem on PlannerField {
      id
      uid
      value
      parent
      children
      isComplete
      updatedAt
      pseudoFieldUid
      attachments {
        ...attachmentItemV2
        presetAttachment {
          ...attachmentItem
        }
      }
      comments {
        totalCount
        unreadCount
        unreadCountV2
      }
      resolvedMinimalTree {
        ...unitPlanResolvedMinimalTreeItem
      }
      responses: allResponses {
        totalCount
        edges {
          node {
            ...responseItem
          }
        }
      }
    }
    ${responseFragment.response}
    ${resolvedFieldValueFragmentV2.resolvedMinimalTree}
    ${projectAttachmentFragment.attachment}
  `,
  unitPlanAllFields: gql`
    fragment unitPlanAllFields on PlannerField {
      id
      uid
      value
      resolvedMinimalTree {
        ...unitPlanResolvedMinimalTreeItem
      }
    }
    ${resolvedFieldValueFragmentV2.resolvedMinimalTree}
  `,
  fieldsResolvedMinimalTreeFragment: gql`
    fragment fieldsResolvedMinimalTreeFragmentItem on PlannerField {
      id
      uid
      value
      pseudoFieldUid
      children
      isComplete
      attachments {
        ...attachmentItem
      }
      comments {
        unreadCountV2
      }
      resolvedMinimalTree {
        id
        type
        ... on ResolvedFieldPlannerElementSet {
          id
          type
          ... on ResolvedFieldPlannerElementSet {
            id
            type
            nodes {
              id
              label
              type
              metadata
            }
          }
        }
      }
    }
    ${attachmentFragment.attachment}
  `,
};

export const unitPlanResourceLibraryFragment = {
  unitPlanResourceLibrary: gql`
    fragment unitPlanResourceLibraryItem on UnitPlan {
      id
      resourceLibrary(filters: $filters) {
        leCount
        assessmentCount
        worksheetCount
        edges {
          type
          node {
            ...resourceLibraryItem
          }
        }
      }
    }
    ${resourceLibraryFragment.resourceFeed}
  `,
};

export const resourcePlannerFragment = {
  resource: gql`
    fragment resourcePlannerItem on PlannerResource {
      id
      title
      type
      description
      usedCount
      createdBy {
        ...staffItem
      }
      attachments {
        ...attachmentItem
      }
    }
    ${attachmentFragment.attachment}
    ${staffFragment.basicStaffDetails}
  `,
};

export const resourceConnectionFragment = {
  resourceConnection: gql`
    fragment resourceConnectionItem on PlannerResourceConnection {
      totalCount
      edges {
        node {
          ...resourcePlannerItem
        }
      }
    }
    ${resourcePlannerFragment.resource}
  `,
};

export const staffDetailUnitPlanFeedFragment = {
  staffUnitPlanFeedCount: gql`
    fragment staffUnitPlanFeedCountItem on Staff {
      id
      unitPlans(filters: $filters) {
        totalCount
      }
    }
  `,
  staffDetailUnitPlanFeed: gql`
    fragment staffDetailUnitPlanFeedItem on Staff {
      ...staffItem
      unitPlans(filters: $filters, groupBy: $groupBy) {
        totalCount
        edges {
          fieldValue
          type
          node {
            ...unitPlanItem
          }
        }
      }
    }
    ${staffFragment.basicStaffDetails}
    ${unitPlanFeedFragment.unitPlanFeed}
  `,
};

export const courseDetailUnitPlanFeedFragment = {
  courseUnitPlanFeedCount: gql`
    fragment courseUnitPlanFeedCountItem on Course {
      id
      unitPlans(filters: $filters) {
        totalCount
      }
    }
  `,
  courseUnitPlanFeed: gql`
    fragment courseUnitPlanFeedItem on Course {
      id
      unitPlans(filters: $filters, groupBy: $groupBy) {
        totalCount
        edges {
          fieldValue
          type
          node {
            ...unitPlanItem
          }
        }
      }
    }
    ${unitPlanFeedFragment.unitPlanFeed}
  `,
  courseFlatUnitPlanFeed: gql`
    fragment courseFlatUnitPlanFeedItem on Course {
      id
      flatUnitPlans(
        first: $first
        after: $after
        filters: $filters
        orderBy: $orderBy
        orderByDirection: $orderByDirection
      ) {
        totalCount
        unitTypeCount {
          type
          count
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...unitPlanFeedWithExceptionalCollaboratorsItem
          }
        }
      }
    }
    ${unitPlanFeedFragment.unitPlanFeedWithExceptionalCollaborators}
  `,
  courseFlatFeedUnitPlanV2: gql`
    fragment courseFlatFeedUnitPlanItem on Course {
      id
      flatUnitPlans(
        first: $first
        after: $after
        filters: $filters
        orderBy: $orderBy
        groupBy: $groupBy
        orderByDirection: $orderByDirection
      ) {
        totalCount
        unitTypeCount {
          type
          count
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...unitPlanFeedItem
          }
        }
      }
    }
    ${unitPlanFeedFragment.unitPlanFeedV2}
  `,
  courseFlatFeedUnitPlan: gql`
    fragment courseFlatFeedUnitPlan on Course {
      id
      flatUnitPlans(
        first: $first
        after: $after
        orderBy: $orderBy
        orderByDirection: $orderByDirection
      ) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            ...unitPlanFeedItem
          }
        }
      }
    }
    ${unitPlanFeedFragment.unitPlanCustomFeed}
  `,
  courseFlatUnitPlanTitle: gql`
    fragment courseFlatUnitPlanTitleItem on Course {
      id
      flatUnitPlans(
        after: $after
        filters: $filters
        orderBy: $orderBy
        orderByDirection: $orderByDirection
      ) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            title {
              id
              value
            }
          }
        }
      }
    }
  `,
};

export const unitPlanFragment = {
  unitPlanFields: gql`
    fragment unitPlanFields on UnitPlan {
      id
      allFields {
        id
        uid
        value
      }
    }
  `,
  unitPlanDetail: gql`
    fragment unitPlanItem on UnitPlan {
      id
      state
      templateId
      defaultStaffAccess
      isSharedWithStudents
      isSharedWithFamilies
      enabledSections {
        userType
        sections
      }
      title {
        id
        value
      }
      image {
        id
        value
        resolvedMinimalTree {
          ...unitPlanResolvedMinimalTreeItem
        }
      }
      unitType {
        id
        value
      }
      duration {
        id
        value
      }
      courses {
        id
        title
      }
      owner {
        id
      }
      resourceLibrary {
        leCount
        assessmentCount
      }
      theme {
        id
        value
      }
      subjects {
        id
        value
      }
      fields(uids: $uids) {
        id
        uid
        value
        resolvedMinimalTree {
          ...unitPlanResolvedMinimalTreeItem
        }
      }
      curriculumProgram {
        id
        nodeInterfaceType
        type
      }
      collaborators: allCollaborators {
        totalCount
        isEditPermission
        edges {
          permission
          node {
            ...userNameFields
            profileImage
          }
        }
      }
      exceptionalCollaborators(filters: $exceptionalCollaboratorsFilter) {
        totalCount
        edges {
          node {
            id
          }
          permission
        }
      }
      isPublishedInSampleUnits
      isImported
      academicYears {
        id
        startDate
        endDate
        isCurrentAcademicYear
      }
      taggedPractices {
        ...taggedPracticesOnEvidence
      }
    }
    ${userNameFragment}
    ${resolvedFieldValueFragmentV2.resolvedMinimalTree}
    ${taggedPracticesFragment.taggedPractices}
  `,
};

export const plannerFieldsOfNodeFragment = {
  unitPlan: gql`
    fragment unitPlanNodeItem on UnitPlan {
      id
      fields(uids: $uids) {
        ...unitPlanFieldItemWithResponseFilter
      }
    }
    ${plannerFieldFragment.unitPlanFieldWithResponseFilter}
  `,
  assessment: gql`
    fragment assessmentNodeItem on Assessment {
      id
      fields(uids: $uids) {
        ...resourceLibraryFieldItem
      }
    }
    ${plannerFieldFragment.resourceLibraryField}
  `,
  le: gql`
    fragment leNodeItem on LearningEngagement {
      id
      fields(uids: $uids) {
        ...resourceLibraryFieldItem
      }
    }
    ${plannerFieldFragment.resourceLibraryField}
  `,
  project: gql`
    fragment projectNodeItem on Project {
      id
      fields(filters: { uids: $uids }) {
        ...projectFieldItem
      }
    }
    ${plannerFieldFragment.projectFieldItem}
  `,
};

export const resourceNodeFragment = {
  unitPlan: gql`
    fragment unitPlanNodeItem on UnitPlan {
      id
      resources {
        ...resourceConnectionItem
      }
    }
    ${resourceConnectionFragment.resourceConnection}
  `,
  assessment: gql`
    fragment assessmentNodeItem on Assessment {
      id
      resources {
        ...resourceConnectionItem
      }
    }
    ${resourceConnectionFragment.resourceConnection}
  `,
  le: gql`
    fragment leNodeItem on LearningEngagement {
      id
      resources {
        ...resourceConnectionItem
      }
    }
    ${resourceConnectionFragment.resourceConnection}
  `,
};

export const plannerOrganizationResourceNodeFragment = {
  unitPlanOrganizationResourceNode: gql`
    fragment unitPlanOrganizationResourceNodeItem on UnitPlan {
      id
      organizationResourcesV2: organizationResourcesConnection(first: $first) {
        edges {
          node {
            ...organizationResourceItem
          }
        }
      }
    }
    ${organizationResourceFragment.organizationResource}
  `,
  assessmentOrganizationResourceNode: gql`
    fragment assessmentOrganizationResourceNodeItem on Assessment {
      id
      organizationResourcesV2(
        orderBy: $orderBy
        orderByDirection: $orderByDirection
        first: $first
        filters: $filters
      ) {
        edges {
          node {
            ...organizationResourceItem
          }
        }
      }
    }
    ${organizationResourceFragment.organizationResource}
  `,
};

export const templateFieldConnectionFragment = {
  templateFieldConnection: gql`
    fragment templateFieldConnectionItem on TemplateFieldConnection {
      edges {
        node {
          id
          uid
          config(filter: $templateFieldConfigFilter) {
            id
            globalFieldProperty {
              id
              property
            }
            parentId
            children
            values(filters: $fieldPropertyValuesFilters) {
              id
              value
              configLevel {
                id
                type
              }
            }
          }
        }
      }
    }
  `,
};

export const unitTypeFragment = {
  unitType: gql`
    fragment unitTypeItem on UnitType {
      id
      feedLabel
      name
      curriculum {
        id
        label
        type
      }
    }
  `,
};

const atlsCategoryFragment = {
  basicAtlCategoryDetails: gql`
    fragment atlCategoryItem on ATLCategory {
      id
      label
      subText
      grades {
        id
      }
    }
  `,
};

export const unitPlanGradeFragment = {
  gradeDetails: gql`
    fragment gradeItem on Grade {
      id
      name
      subjects(filters: { archivalState: ALL }) {
        ...subjectItem
        subjectGroup {
          id
          name
          constants
          globalSubjectGroup {
            id
            name
            constants
          }
        }
      }
      atlCategories {
        ...atlCategoryItem
      }
    }
    ${subjectGroupFragment.subjectBenchmarkDetails}
    ${atlsCategoryFragment.basicAtlCategoryDetails}
  `,
};
